"use client";

import React, { isValidElement, useState } from "react";
import { cn } from "@/lib/utils";

const COLLAPSE_CHAR_LIMIT = 520;
const COLLAPSE_WORD_LIMIT = 90;

const extractText = (node: React.ReactNode): string => {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractText).join(" ");
  if (isValidElement(node)) {
    return extractText((node as React.ReactElement<{ children?: React.ReactNode }>).props?.children);
  }
  return "";
};

export const shouldCollapseHighlight = (description: React.ReactNode): boolean => {
  const text = extractText(description).replace(/\s+/g, " ").trim();
  if (!text) return false;
  return text.length > COLLAPSE_CHAR_LIMIT || text.split(" ").length > COLLAPSE_WORD_LIMIT;
};

export type CollapseMode = "auto" | "always" | "never";

export const isHighlightCollapsible = (description: React.ReactNode, mode: CollapseMode) =>
  mode === "never" ? false : mode === "always" ? true : shouldCollapseHighlight(description);

export const HighlightCard = ({
  title,
  description,
  icon,
  headingLevel,
  collapsible,
  readMoreLabel,
  readLessLabel,
}: {
  title: string;
  description: React.ReactNode;
  icon?: React.ReactNode;
  headingLevel: "h2" | "h3";
  collapsible: boolean;
  readMoreLabel: string;
  readLessLabel: string;
}) => {
  const [expanded, setExpanded] = useState(false);
  const isCollapsed = collapsible && !expanded;
  const Heading = headingLevel;

  return (
    <div className="rounded-card bg-white/80 p-6 shadow-card backdrop-blur dark:bg-surface-dark-raised/80 dark:shadow-card-dark">
      {icon && (
        <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-navy/10 text-brand-navy dark:bg-white/10 dark:text-white">
          {icon}
        </div>
      )}
      <Heading className="mb-2 text-xl font-bold text-brand-navy dark:text-white">{title}</Heading>
      <div className="relative">
        {typeof description === "string" ? (
          <p
            className={cn(
              "whitespace-pre-line text-gray-600 dark:text-gray-300",
              isCollapsed && "max-h-48 overflow-hidden",
            )}
          >
            {description}
          </p>
        ) : (
          <div
            className={cn(
              "space-y-4 text-gray-600 dark:text-gray-300",
              isCollapsed && "max-h-48 overflow-hidden",
            )}
          >
            {description}
          </div>
        )}
        {isCollapsed && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white/95 via-white/85 to-transparent dark:from-surface-dark-raised/95 dark:via-surface-dark-raised/80" />
        )}
      </div>
      {collapsible && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-4 inline-flex items-center rounded-full border border-brand-navy/20 px-4 py-2 text-sm font-semibold text-brand-navy transition hover:bg-brand-navy/5 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
          aria-expanded={expanded}
        >
          {expanded ? readLessLabel : readMoreLabel}
        </button>
      )}
    </div>
  );
};
